import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Package,
  Truck,
  Calendar,
  MoreVertical,
  Undo2,
  ChevronRight,
  AlertTriangle,
  Hash,
  User,
  Filter,
  Eye
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import Navigation from "../../components/Navigation";
import ScanTabs from "../../components/purchasing/ScanTabs";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ReceivedOrdersPage() {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showUndoDialog, setShowUndoDialog] = useState(false);
  const [undoingReceipt, setUndoingReceipt] = useState(null);
  const [detailsFilter, setDetailsFilter] = useState("all"); // "all" or "with_reservations"

  useEffect(() => {
    fetchDeliveryReceipts();
  }, []);

  const fetchDeliveryReceipts = async () => {
    try {
      const response = await axios.get(`${API}/purchasing/delivery-receipts`);
      setReceipts(response.data);
    } catch (error) {
      console.error("Error fetching delivery receipts:", error);
      toast.error("Kunde inte hämta leveransmottagningar");
    } finally {
      setLoading(false);
    }
  };

  const fetchReceiptDetails = async (receiptId) => {
    try {
      const response = await axios.get(`${API}/purchasing/delivery-receipts/${receiptId}`);
      setSelectedReceipt(response.data);
      setDetailsFilter("all");
      setShowDetailsDialog(true);
    } catch (error) {
      toast.error("Kunde inte hämta detaljer");
    }
  };

  const handleUndoReceipt = async () => {
    if (!undoingReceipt) return;
    
    try {
      const response = await axios.post(`${API}/purchasing/delivery-receipts/${undoingReceipt.id}/undo`);
      toast.success(response.data.message);
      setShowUndoDialog(false);
      setUndoingReceipt(null);
      fetchDeliveryReceipts();
    } catch (error) {
      toast.error("Kunde inte ångra mottagning: " + (error.response?.data?.detail || error.message));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getTotalItems = (receipt) => {
    return receipt.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || receipt.total_items || 0;
  };

  // Filter items based on selected filter
  const getFilteredItems = () => {
    if (!selectedReceipt?.items) return [];
    
    if (detailsFilter === "with_reservations") {
      // Only show items that had reservations (have customer_order_number)
      return selectedReceipt.items.filter(item => 
        item.customer_order_number || item.order_number
      );
    }
    
    return selectedReceipt.items;
  };

  const filteredItems = getFilteredItems();

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/purchasing">
              <Button variant="ghost" size="icon" data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="font-heading text-xl font-bold uppercase tracking-tight">
                Mottagna leveranser
              </h1>
              <p className="text-sm text-muted-foreground">
                {loading ? "Laddar..." : `${receipts.length} leveransmottagningar`}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* Page Switcher */}
        <ScanTabs />

        {/* Receipts List */}
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="animate-spin text-4xl mb-4">📦</div>
              <p className="text-muted-foreground">Laddar leveransmottagningar...</p>
            </CardContent>
          </Card>
        ) : receipts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Inga leveransmottagningar ännu</p>
              <Link to="/purchasing/orders">
                <Button variant="link" className="mt-2">
                  Gå till beställda produkter
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {receipts.map((receipt) => (
              <Card 
                key={receipt.id} 
                className="hover:border-primary/50 transition-colors cursor-pointer"
                data-testid={`receipt-${receipt.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex-1 flex items-center gap-4"
                      onClick={() => fetchReceiptDetails(receipt.id)}
                    >
                      <div className="p-2 rounded-lg bg-success/10">
                        <Package className="h-5 w-5 text-success" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">
                            {receipt.supplier_name || "Okänd leverantör"}
                          </span>
                          <Badge variant="outline" className="text-success border-success/50">
                            Mottagen
                          </Badge>
                          {receipt.source === "quick_receive" && (
                            <Badge variant="secondary" className="text-xs">
                              Snabbmottagning
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(receipt.received_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Hash className="h-3 w-3" />
                            {getTotalItems(receipt)} st ({receipt.items?.length || 0} artiklar)
                          </span>
                          {receipt.received_by && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {receipt.received_by}
                            </span>
                          )}
                          {receipt.freight_cost > 0 && (
                            <span className="flex items-center gap-1">
                              <Truck className="h-3 w-3" />
                              Frakt: {receipt.freight_cost} kr
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                    
                    {/* Actions Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" data-testid={`receipt-menu-${receipt.id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchReceiptDetails(receipt.id);
                          }}
                          data-testid={`view-details-${receipt.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Visa detaljer
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setUndoingReceipt(receipt);
                            setShowUndoDialog(true);
                          }}
                          className="text-destructive focus:text-destructive"
                          data-testid={`undo-receipt-${receipt.id}`}
                        >
                          <Undo2 className="h-4 w-4 mr-2" />
                          Ångra mottagning
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Receipt Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-success" />
              Leverans från {selectedReceipt?.supplier_name}
            </DialogTitle>
            <DialogDescription>
              Mottagen {formatDate(selectedReceipt?.received_at)} av {selectedReceipt?.received_by}
            </DialogDescription>
          </DialogHeader>
          
          {selectedReceipt && (
            <div className="space-y-4">
              {/* Filter Buttons */}
              <div className="flex gap-2">
                <Button
                  variant={detailsFilter === "all" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDetailsFilter("all")}
                  data-testid="filter-all"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Alla ({selectedReceipt.items?.length || 0})
                </Button>
                <Button
                  variant={detailsFilter === "with_reservations" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDetailsFilter("with_reservations")}
                  data-testid="filter-reservations"
                >
                  Med reservationer ({selectedReceipt.items?.filter(i => i.customer_order_number || i.order_number).length || 0})
                </Button>
              </div>
              
              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium w-16">Antal</th>
                      <th className="text-left p-3 text-sm font-medium">Artikelnummer</th>
                      <th className="text-left p-3 text-sm font-medium">EAN</th>
                      <th className="text-left p-3 text-sm font-medium">Referensnummer</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredItems.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="p-4 text-center text-muted-foreground">
                          {detailsFilter === "with_reservations" 
                            ? "Inga produkter med reservationer"
                            : "Inga produkter"}
                        </td>
                      </tr>
                    ) : (
                      filteredItems.map((item, idx) => {
                        // Split customer_order_number into individual numbers
                        const refNumbers = (item.customer_order_number || item.order_number || "")
                          .split(",")
                          .map(n => n.trim())
                          .filter(n => n);
                        
                        return (
                          <tr key={idx} className="hover:bg-muted/30">
                            <td className="p-3">
                              <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-md bg-primary/10 text-primary font-mono font-semibold text-sm">
                                {item.quantity ?? "—"}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className="font-medium">{item.artikelnummer || "—"}</span>
                            </td>
                            <td className="p-3">
                              <span className="font-mono text-sm">{item.ean || "—"}</span>
                            </td>
                            <td className="p-3">
                              {refNumbers.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {refNumbers.map((num, i) => (
                                    <span 
                                      key={i}
                                      className="font-mono text-sm text-purple-400 select-all cursor-text"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {num}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Summary */}
              <div className="text-sm text-muted-foreground text-center">
                Visar {filteredItems.length} av {selectedReceipt.items?.length || 0} produkter
                {" · "}
                <span className="text-foreground font-medium">
                  Totalt {filteredItems.reduce((s, i) => s + (i.quantity || 0), 0)} st
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Undo Confirmation Dialog */}
      <AlertDialog open={showUndoDialog} onOpenChange={setShowUndoDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Ångra mottagning?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-2">
                Detta kommer att återställa systemet till läget före mottagningen:
              </span>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Ta bort mottagna produkter från lagret</li>
                <li>Ta bort eventuella bokningar som skapades</li>
                <li>Återställa beställningar till tidigare status</li>
              </ul>
              <span className="block mt-3 font-medium text-destructive">
                Denna åtgärd kan inte ångras!
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUndoReceipt}
              className="bg-destructive hover:bg-destructive/90"
            >
              Ja, ångra mottagning
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
